import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import type { TournamentPlayerEntryResponse } from "@bw/shared/types/generated";

/**
 * The WITHDRAW confirmation (registration S4), opened from the read-only
 * registered-status surface. Confirms the player wants to withdraw their entry.
 *
 * Shows (a) WHAT they are withdrawing from — the event/draw DISPLAY name
 * (event_name ?? draw_name), never a uuid — and (b) the refund POLICY summary
 * (tournament.refund_policy_summary) DISPLAY-ONLY. It NEVER computes, invents, or
 * echoes a refund amount/date: even though the withdraw action response carries a
 * server-computed refund_amount, the FE shows only the policy text — the refund
 * itself is the backend's concern (#1402).
 *
 * Confirm calls the withdraw seam with the entry_id; the button is disabled the
 * moment the user commits (and while the parent mutation is pending), so a
 * double-click fires EXACTLY ONE withdraw. A failed withdraw keeps the dialog open
 * with an honest role="alert" (the server message) and does NOT flip the status —
 * the parent only flips it on the server's refetched truth.
 *
 * Mirrors the calendar CancelBookingDialog's WAI-ARIA modal pattern (focus the
 * non-destructive control on open, Escape/scrim dismiss except mid-mutation).
 */
export function TournamentWithdrawDialog({
  entry,
  refundPolicySummary,
  onConfirm,
  onClose,
  withdrawPending,
  withdrawError,
}: {
  entry: TournamentPlayerEntryResponse;
  refundPolicySummary: string | null | undefined;
  onConfirm: () => void;
  onClose: () => void;
  withdrawPending: boolean;
  withdrawError: unknown;
}) {
  // The display name comes from event_name, falling back to draw_name, then an
  // em-dash — never a uuid. The same permitted fallback the S0 status uses.
  const displayName = entry.event_name ?? entry.draw_name ?? "—";

  // Latched the moment the user commits to the withdraw. `withdrawPending` (owned
  // by the parent mutation) is the authoritative in-flight signal, but the click
  // is the earliest one — so "confirm has been clicked" stays locked until the
  // withdraw errors (the dialog stays open to retry) or the parent unmounts the
  // dialog on success. This guarantees a second click / Escape / scrim can never
  // fire a SECOND withdraw or tear the dialog down between the click and result.
  const [confirmInitiated, setConfirmInitiated] = useState(false);
  const locked = withdrawPending || confirmInitiated;

  // A failed withdraw re-opens the door: the dialog stays mounted so the user may
  // dismiss it or retry — release the latch.
  useEffect(() => {
    if (withdrawError) setConfirmInitiated(false);
  }, [withdrawError]);

  function handleConfirm() {
    if (locked) return;
    setConfirmInitiated(true);
    onConfirm();
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!locked) onClose();
      }}
      aria-labelledby="withdraw-dialog-title"
      aria-describedby="withdraw-refund-policy"
      slotProps={{
        paper: {
          sx: { width: "min(100%, 32rem)" },
        },
        backdrop: {
          "data-testid": "withdraw-dialog-backdrop",
        } as Record<string, string>,
      }}
    >
      <DialogTitle
        id="withdraw-dialog-title"
        aria-label="Withdraw from this tournament"
      >
        Withdraw your entry?
      </DialogTitle>

      <DialogContent>
        <Typography>
          You&apos;re withdrawing from{" "}
          <Box component="span" sx={{ fontWeight: 800 }}>
            {displayName}
          </Box>
          .
        </Typography>

        {/* The refund POLICY, display-only. NO client-computed refund amount, no
            "you'll get $X back" — the backend applies the refund per this policy. */}
        {refundPolicySummary ? (
          <Paper
            component="section"
            id="withdraw-refund-policy"
            variant="outlined"
            aria-label="Refund policy"
            sx={{ mt: 2, p: 2 }}
          >
            <Typography component="h3" variant="subtitle2" gutterBottom>
              Refund policy
            </Typography>
            <Typography color="text.secondary">
              {refundPolicySummary}
            </Typography>
          </Paper>
        ) : null}

        {/* A failed withdraw → an honest alert (the server message). The dialog
            stays open; the parent never flips the status on a failure. */}
        {withdrawError ? (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {mapWithdrawError(withdrawError)}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button
          autoFocus
          type="button"
          size="small"
          onClick={onClose}
          disabled={locked}
        >
          Keep entry
        </Button>
        <Button
          type="button"
          size="small"
          color="error"
          variant="contained"
          onClick={handleConfirm}
          disabled={locked}
          aria-busy={locked || undefined}
        >
          {locked ? "Withdrawing…" : "Withdraw"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Withdraw error → player copy. The backend's 4xx detail is a bare string (the
// ApiError code is UNKNOWN_ERROR, carrying no signal), so the server MESSAGE is
// surfaced verbatim — it is the honest reason (e.g. a 409 "this entry has
// advanced and can no longer be withdrawn"). A non-ApiError falls back to a
// generic retry line.
// ---------------------------------------------------------------------------

function mapWithdrawError(error: unknown): string {
  if (isApiError(error)) {
    return (
      error.message || "We couldn't withdraw your entry. Please try again."
    );
  }
  return "We couldn't withdraw your entry. Please try again.";
}
